/* eslint-disable react-hooks/exhaustive-deps */
import { Upload, X } from "lucide-react";
import React, { useEffect } from "react";
import {
  ComponentInputs,
  DocumentFileMap,
  SingleTemplate,
} from "../../models/whatsappTemplate.model";
import {
  clearFileNameAndUrl,
  // updateTemplateConfig,
} from "../../store/slices/roundRobinSlice";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";

interface TemplateInputConfiguratorProps {
  singleTemplate: SingleTemplate;
  documentFile: DocumentFileMap;
  setDocumentFile: React.Dispatch<React.SetStateAction<DocumentFileMap>>;
  setComponentInputs: React.Dispatch<React.SetStateAction<ComponentInputs>>;
  handleSelectFile: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  componentInputs: ComponentInputs;
  storedDocumentUrl?: string;
  storedDocumentFilename?: string;
  setConfig?: React.Dispatch<React.SetStateAction<any>>;
}

const getDynamicComponents = (componentInputs: ComponentInputs) => {
  const headers: string[] = [];
  const body: string[] = [];
  const footer: string[] = [];

  Object.keys(componentInputs).forEach((key) => {
    const value = componentInputs[key];
    if (!value) return;

    if (key.startsWith("HEADER_")) headers.push(value);
    else if (key.startsWith("BODY_")) body.push(value);
    else if (key.startsWith("FOOTER_")) footer.push(value);
  });

  return { headers, body, footer };
};

const GenerateTemplateConfiguration: React.FC<
  TemplateInputConfiguratorProps
> = ({
  singleTemplate,
  documentFile,
  setDocumentFile,
  setComponentInputs,
  handleSelectFile,
  inputRef,
  componentInputs,
  storedDocumentUrl,
  storedDocumentFilename,
  setConfig,
}) => {
  const isTemplateLoading = useSelector(
    (state: RootState) => state.whatsAppData.loading,
  );
  const configData = useSelector(
    (state: RootState) => state.roundRobin.configInfo,
  );
  const isCronJobRunning = useSelector(
    (state: RootState) => state.roundRobin.cronJobStatus,
  );
  const components = React.useMemo(
    () => singleTemplate?.template?.components || [],
    [singleTemplate],
  );
  const isPaused = useSelector((state: RootState) => state.roundRobin.isPaused);

  const disable = !!isTemplateLoading || (!!isCronJobRunning && !isPaused);
  const isHelloWorld = singleTemplate?.template?.name === "hello_world";
  const dispatch: RootDispatch = useDispatch();

  // Effect to restore document file state when component mounts or stored info changes
  useEffect(() => {
    if (storedDocumentUrl && storedDocumentFilename) {
      // Find the document/image header component
      const headerComponent = components.find(
        (comp) =>
          comp.type.toUpperCase() === "HEADER" &&
          (comp.format === "DOCUMENT" || comp.format === "IMAGE"),
      );

      if (headerComponent) {
        const type = headerComponent.type.toUpperCase();

        const mockFile = {
          name: storedDocumentFilename,
          url: storedDocumentUrl,
          isStored: true,
        } as any;

        setDocumentFile((prev) => ({
          ...prev,
          [type]: mockFile,
        }));
      }
    }
  }, [storedDocumentUrl, storedDocumentFilename, components, setDocumentFile]);
  useEffect(() => {
    if (!configData) return;

    const newComponentInputs: ComponentInputs = {};

    configData.headers?.forEach((val, index) => {
      newComponentInputs[`HEADER_${index + 1}`] = val;
    });

    configData.body?.forEach((val, index) => {
      newComponentInputs[`BODY_${index + 1}`] = val;
    });

    configData.footer?.forEach((val, index) => {
      newComponentInputs[`FOOTER_${index + 1}`] = val;
    });

    if (Object.keys(newComponentInputs).length > 0) {
      setComponentInputs((prev) => ({ ...prev, ...newComponentInputs }));
    }
  }, [configData, setComponentInputs]);

  if (!components.length || isHelloWorld) return null;

  const handleRemoveFile = (type: string) => {
    setDocumentFile((prev) => {
      const newFileMap = { ...prev };
      delete newFileMap[type];
      return newFileMap;
    });
    dispatch(clearFileNameAndUrl());
  };

  return (
    <div className="space-y-3">
      <div className="text-xs font-medium text-gray-600 border-b pb-1">
        Template Configuration
      </div>

      {components.map((component, index) => {
        const type = component.type.toUpperCase();
        const format = component.format || "TEXT";
        const text = component.text || "";

        // Document/Image Upload UI
        if (
          type === "HEADER" &&
          (format === "DOCUMENT" || format === "IMAGE")
        ) {
          const currentFile = documentFile && documentFile[type];
          const isStoredFile = currentFile && (currentFile as any).isStored;

          return (
            <div key={index} className="border border-gray-200 rounded-md p-3">
              <div className="text-xs font-medium text-gray-600 mb-2">
                {format === "DOCUMENT" ? "Document" : "Image"}
              </div>

              {currentFile ? (
                <div
                  className={`flex items-center justify-between p-2 rounded text-xs ${
                    isStoredFile
                      ? "bg-blue-50 border border-blue-200"
                      : "bg-green-50 border border-green-200"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate">{currentFile.name}</span>
                    {isStoredFile && (
                      <span className="text-blue-600 text-xs font-medium">
                        (Stored)
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveFile(type)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => {
                    handleSelectFile();
                  }}
                  className={`
    border-2 border-dashed rounded-md p-4 flex flex-col items-center justify-center transition-colors
    ${
      disable
        ? "border-gray-200 cursor-not-allowed bg-gray-100 opacity-60"
        : "border-gray-300 hover:border-blue-400 cursor-pointer"
    }
  `}
                >
                  <Upload className="h-4 w-4 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">Choose file</span>
                  <input
                    disabled={disable}
                    ref={inputRef}
                    type="file"
                    accept={
                      format === "DOCUMENT"
                        ? ".pdf,.doc,.docx"
                        : ".jpg,.jpeg,.png"
                    }
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setDocumentFile((prev) => ({
                        ...prev,
                        [type]: file,
                      }));
                    }}
                    className="sr-only disabled:cursor-not-allowed"
                  />
                </div>
              )}
            </div>
          );
        }

        // Text Parameters Input UI
        const paramMatches = text.match(/{{\s*\d+\s*}}/g) || [];
        if (paramMatches.length > 0) {
          return (
            <div key={index} className="border border-gray-200 rounded-md p-3">
              <div className="text-xs font-medium text-gray-600 mb-2">
                {type === "HEADER" ? "Header" : type === "BODY" ? "Body" : type}{" "}
                Parameters
              </div>
              <div className="space-y-2">
                {paramMatches.map((param, i) => {
                  const paramNum = param.replace(/[^0-9]/g, "");
                  const key = `${type}_${i + 1}`;
                  return (
                    <div key={key}>
                      <label className="block text-xs text-gray-500 mb-1">
                        Parameter {paramNum}
                      </label>
                      <input
                        disabled={disable}
                        type="text"
                        placeholder={`Value for {{${paramNum}}}`}
                        value={componentInputs[key] || ""}
                        onChange={(e) => {
                          const newVal = e.target.value;

                          // Step 1: update local state
                          const updatedInputs = {
                            ...componentInputs,
                            [key]: newVal,
                          };
                          setComponentInputs(updatedInputs);

                          // Step 2: immediately compute and update Redux config
                          const { headers, body, footer } =
                            getDynamicComponents(updatedInputs);

                          setConfig!((prev: any) => ({
                            ...prev,
                            headers,
                            body,
                            footer,
                          }));
                        }}
                        className="w-full px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};

export default GenerateTemplateConfiguration;
